import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { db, isConfigured } from '../firebaseConfig';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where, serverTimestamp } from 'firebase/firestore';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    tag: 'Website',
    img: '',
    alternative: '',
    description: '',
    link: '',
    more: '',
    clientName: 'Personal',
    clientEmail: '',
    status: 'Completed'
  });

  useEffect(() => {
    if (isConfigured) {
      fetchProjects();
      fetchClients();
    }
  }, []);

  const fetchProjects = async () => {
    if (!isConfigured) return;
    try {
      const querySnapshot = await getDocs(collection(db, 'projects'));
      const projectsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjects(projectsList);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchClients = async () => {
    if (!isConfigured) return;
    try {
      const querySnapshot = await getDocs(collection(db, 'clients'));
      const clientsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(clientsList);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClientChange = (e) => {
    const selectedClientName = e.target.value;
    const selectedClient = clients.find(c => c.client_name === selectedClientName);
    
    setFormData(prev => ({
      ...prev,
      clientName: selectedClientName,
      clientEmail: selectedClient ? selectedClient.client_email : ''
    }));
  };

  const sendReviewRequestEmail = async (projectTitle, clientEmail, clientName) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://portfolioback-self.vercel.app/';
      
      const response = await fetch(`${apiUrl}send-review-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectTitle,
          clientEmail,
          clientName,
          reviewLink: `${window.location.origin}/add-review?project=${encodeURIComponent(projectTitle)}&client=${encodeURIComponent(clientName)}`
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        console.log('Review request email sent to:', clientEmail);
        return true;
      } else {
        console.error('Email sending failed:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Error sending email:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isConfigured) {
      alert('Firebase is not configured. Please set up your Firebase configuration first.');
      return;
    }
    
    try {
      const wasIncomplete = editingProject && editingProject.status !== 'Completed';
      const isNowCompleted = formData.status === 'Completed';
      const hasClient = formData.clientName !== 'Personal';
      
      if (editingProject) {
        await updateDoc(doc(db, 'projects', editingProject.id), {
          ...formData,
          updatedAt: serverTimestamp()
        });

        
        // Send email if project was just marked as completed and has a client
        if (wasIncomplete && isNowCompleted && hasClient && formData.clientEmail) {
          console.log('🔄 Attempting to send review email...');
          console.log('📧 To:', formData.clientEmail);
          console.log('👤 Client:', formData.clientName);
          console.log('📁 Project:', formData.title);
          
          const emailSent = await sendReviewRequestEmail(
            formData.title,
            formData.clientEmail,
            formData.clientName
          );
          
          if (emailSent) {
            alert('Project updated successfully! Review request email has been sent.');
          } else {
            alert('Project updated successfully! (Email notification failed - check console)');
          }
        } else {
          alert('Project updated successfully!');
        }
      } else {
        await addDoc(collection(db, 'projects'), {
          ...formData,
          createdAt: serverTimestamp()
        });

        
        // Send email if new project is already completed and has a client
        if (isNowCompleted && hasClient && formData.clientEmail) {
          console.log('🔄 Attempting to send review email for new project...');
          const emailSent = await sendReviewRequestEmail(
            formData.title,
            formData.clientEmail,
            formData.clientName
          );
          
          if (emailSent) {
            alert('Project added successfully! Review request email has been sent.');
          } else {
            alert('Project added successfully! (Email notification failed - check console)');
          }
        } else {
          alert('Project added successfully!');
        }
      }
      
      setFormData({
        title: '',
        tag: 'Website',
        img: '',
        alternative: '',
        description: '',
        link: '',
        more: '',
        clientName: 'Personal',
        clientEmail: '',
        status: 'Completed'
      });
      setShowAddForm(false);
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project. Please try again.');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title || '',
      tag: project.tag || 'Website',
      img: project.img || '',
      alternative: project.alternative || '',
      description: project.description || '',
      link: project.link || '',
      more: project.more || '',
      clientName: project.clientName || 'Personal',
      clientEmail: project.clientEmail || '',
      status: project.status || 'Completed'
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteDoc(doc(db, 'projects', id));
        alert('Project deleted successfully!');
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Error deleting project. Please try again.');
      }
    }
  };

  const cancelEdit = () => {
    setShowAddForm(false);
    setEditingProject(null);
    setFormData({
      title: '',
      tag: 'Website',
      img: '',
      alternative: '',
      description: '',
      link: '',
      more: '',
      clientName: 'Personal',
      clientEmail: '',
      status: 'Completed'
    });
  };

  return (
    <div className='flex min-h-screen bg-[#FFB7CE]'>
      <Sidebar />
      <div className='flex-1 p-4 pt-20 lg:pt-4 lg:pl-8'>
        <div className='max-w-7xl mx-auto'>
          {!isConfigured && (
            <div className='bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded'>
              <p className='font-bold'>⚠️ Firebase Not Configured</p>
              <p className='text-sm mt-2'>
                Please set up your Firebase configuration in <code className='bg-yellow-200 px-2 py-1 rounded'>src/firebaseConfig.js</code> to enable database functionality.
              </p>
            </div>
          )}
          
          <div className='flex justify-between items-center mb-6'>
            <h1 className='text-3xl font-bold text-white'>PROJECTS</h1>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className='bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200'
              disabled={!isConfigured}
            >
              {showAddForm ? 'CANCEL' : 'ADD'}
            </button>
          </div>

          {showAddForm && (
            <div className='bg-white rounded-lg p-6 mb-6 shadow-lg'>
              <h2 className='text-2xl font-bold text-[#89CFF0] mb-6'>
                {editingProject ? 'EDIT PROJECT' : 'ADD PROJECT'}
              </h2>
              <div className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>TITLE:</label>
                    <input
                      type='text'
                      name='title'
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    />
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>TAG:</label>
                    <select
                      name='tag'
                      value={formData.tag}
                      onChange={handleInputChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    >
                      <option value='Website'>Website</option>
                      <option value='App'>App</option>
                      <option value='Web'>Web</option>
                      <option value='Design'>Design</option>
                    </select>
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>IMAGE URL:</label>
                    <input
                      type='url'
                      name='img'
                      value={formData.img}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    />
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>IMAGE ALT TEXT:</label>
                    <input
                      type='text'
                      name='alternative'
                      value={formData.alternative}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    />
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>WEB/APP LINK:</label>
                    <input
                      type='url'
                      name='link'
                      value={formData.link}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    />
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>READ MORE LINK:</label>
                    <input
                      type='url'
                      name='more'
                      value={formData.more}
                      onChange={handleInputChange}
                      placeholder='Optional'
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    />
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>CLIENT:</label>
                    <select
                      name='clientName'
                      value={formData.clientName}
                      onChange={handleClientChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    >
                      <option value='Personal'>Personal</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.client_name}>
                          {client.client_name}
                        </option>
                      ))}
                    </select>
                    {formData.clientEmail && (
                      <p className='text-sm text-gray-500 mt-1'>📧 {formData.clientEmail}</p>
                    )}
                  </div>

                  <div>
                    <label className='block text-gray-700 font-medium mb-2'>STATUS:</label>
                    <select
                      name='status'
                      value={formData.status}
                      onChange={handleInputChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                    >
                      <option value='Planning'>Planning</option>
                      <option value='In Progress'>In Progress</option>
                      <option value='Completed'>Completed</option>
                    </select>
                    {formData.status === 'Completed' && formData.clientName !== 'Personal' && (
                      <p className='text-sm text-green-600 mt-1'>
                        ✅ Review request email will be sent to client
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className='block text-gray-700 font-medium mb-2'>DESCRIPTION:</label>
                  <textarea
                    name='description'
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows='4'
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89CFF0]'
                  />
                </div>

                <div className='flex gap-4'>
                  <button
                    onClick={handleSubmit}
                    className='bg-[#89CFF0] hover:bg-[#6BB8E0] text-white px-8 py-2 rounded-lg font-medium transition-colors duration-200'
                  >
                    {editingProject ? 'UPDATE' : 'ADD'}
                  </button>
                  {editingProject && (
                    <button
                      onClick={cancelEdit}
                      className='bg-gray-400 hover:bg-gray-500 text-white px-8 py-2 rounded-lg font-medium transition-colors duration-200'
                    >
                      CANCEL
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className='bg-white rounded-lg overflow-hidden shadow-lg'>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-[#FFB7CE]'>
                  <tr>
                    <th className='px-4 py-3 text-left text-white font-bold'>TAG</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>IMAGE</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>NAME</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>CLIENT</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>LINK</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>STATUS</th>
                    <th className='px-4 py-3 text-left text-white font-bold'>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.length === 0 ? (
                    <tr>
                      <td colSpan='7' className='px-4 py-8 text-center text-gray-500'>
                        No projects yet. Click ADD to create your first project.
                      </td>
                    </tr>
                  ) : (
                    projects.map(project => (
                      <tr key={project.id} className='border-b hover:bg-gray-50'>
                        <td className='px-4 py-3'>
                          <span className='bg-[#89CFF0] text-white px-3 py-1 rounded-full text-sm'>
                            {project.tag}
                          </span>
                        </td>
                        <td className='px-4 py-3'>
                          <img
                            src={project.img}
                            alt={project.alternative}
                            className='w-16 h-16 object-cover rounded'
                          />
                        </td>
                        <td className='px-4 py-3 font-medium'>{project.title}</td>
                        <td className='px-4 py-3'>
                          <div>
                            <div className='font-medium'>{project.clientName}</div>
                            {project.clientEmail && (
                              <div className='text-xs text-gray-500'>{project.clientEmail}</div>
                            )}
                          </div>
                        </td>
                        <td className='px-4 py-3'>
                          <a
                            href={project.link}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-[#89CFF0] hover:underline'
                          >
                            View
                          </a>
                        </td>
                        <td className='px-4 py-3'>
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            project.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            project.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {project.status}
                          </span>
                        </td>
                        <td className='px-4 py-3'>
                          <div className='flex gap-2'>
                            <button
                              onClick={() => handleEdit(project)}
                              className='bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors duration-200'
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(project.id)}
                              className='bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-colors duration-200'
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}